import { useState, useEffect } from 'react';
import { Database, Plug, Palette, Eye, Keyboard, ShieldAlert, ShieldCheck, Clock, ExternalLink, Activity, Info } from 'lucide-react';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import { BackupPanel } from './BackupPanel';
import { PluginManagerPanel } from './PluginManagerPanel';
import { ThemeToggle } from './ThemeToggle';
import { HighContrastToggle } from './HighContrastToggle';
import { ShortcutCustomizationPanel } from './ShortcutCustomizationPanel';
import { PerformanceDashboard } from './PerformanceDashboard';
import { usePantoneAuth } from '../hooks/usePantoneAuth';
import { PantoneLicenseModal } from './PantoneLicenseModal';
import { useToolState } from '../contexts/ToolStateContext';

type SettingsTab = 'theme' | 'accessibility' | 'shortcuts' | 'backup' | 'plugins' | 'performance';

const aboutConfig = buildAboutConfig(import.meta.env, 'VITE_APP_');

export function SettingsPanel() {
  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('settings');
  const [activeTab, setActiveTab] = useState<SettingsTab>((persisted?.activeTab as SettingsTab) ?? 'theme');
  const { state: pantoneState, login, logout, revokeAccess, acceptLicense, hasAcceptedLicense } = usePantoneAuth();
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  // Persist active tab on change
  useEffect(() => {
    setToolState('settings', { activeTab });
  }, [activeTab, setToolState]);

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Customize appearance, accessibility, keyboard shortcuts, backups, cloud synchronization, and plugin integrations
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
        {/* Narrow viewport: dropdown */}
        <div className="sm:hidden p-2">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as SettingsTab)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="theme">Theme</option>
            <option value="accessibility">Accessibility</option>
            <option value="shortcuts">Shortcuts</option>
            <option value="backup">Backup & Restore</option>
            <option value="plugins">Plugins</option>
            <option value="performance">Performance</option>
          </select>
        </div>
        <div className="hidden sm:flex gap-1 p-2">
          <button
            onClick={() => setActiveTab('theme')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'theme'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Palette className="w-4 h-4" />
            Theme
          </button>
          <button
            onClick={() => setActiveTab('accessibility')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'accessibility'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Eye className="w-4 h-4" />
            Accessibility
          </button>
          <button
            onClick={() => setActiveTab('shortcuts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'shortcuts'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Keyboard className="w-4 h-4" />
            Shortcuts
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'backup'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Database className="w-4 h-4" />
            Backup & Restore
          </button>

          <button
            onClick={() => setActiveTab('plugins')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'plugins'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Plug className="w-4 h-4" />
            Plugins
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'performance'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Activity className="w-4 h-4" />
            Performance
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-b-lg p-6">
        {activeTab === 'theme' && <ThemeToggle />}
        {activeTab === 'accessibility' && <HighContrastToggle />}
        {activeTab === 'shortcuts' && <ShortcutCustomizationPanel />}
        {activeTab === 'backup' && <BackupPanel />}
        {activeTab === 'performance' && <PerformanceDashboard />}
        {activeTab === 'plugins' && (
          <div className="space-y-6">
            <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 space-y-4">
              <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pantone Connect</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Authenticate with Pantone Connect to enable licensed swatch search and nearest-match features.
                  </p>
                </div>
                {pantoneState.offline ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
                    <ShieldAlert className="w-3 h-3" />
                    Offline
                  </span>
                ) : pantoneState.status === 'authenticated' ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                    <ShieldCheck className="w-3 h-3" />
                    Connected
                  </span>
                ) : pantoneState.status === 'authenticating' ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                    <Clock className="w-3 h-3 animate-spin" />
                    Authenticating…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                    <ShieldAlert className="w-3 h-3" />
                    Locked
                  </span>
                )}
              </header>
              {pantoneState.status === 'error' && pantoneState.error && (
                <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/40 rounded-lg p-3">
                  {pantoneState.error}
                </div>
              )}
              <div className="flex flex-wrap gap-3 items-center">
                {pantoneState.status !== 'authenticated' && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!hasAcceptedLicense()) {
                        setLicenseOpen(true);
                        setPendingLogin(true);
                      } else {
                        void login();
                      }
                    }}
                    disabled={pantoneState.offline || pantoneState.status === 'authenticating'}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Connect to Pantone
                  </button>
                )}
                {pantoneState.status === 'authenticated' && (
                  <>
                    <button
                      type="button"
                      onClick={() => logout()}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      Sign out
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (confirm('Revoke Pantone Connect access? You will need to re-authenticate to use licensed features.')) revokeAccess('user-initiated'); }}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <ShieldAlert className="w-4 h-4" />
                      Revoke Access
                    </button>
                  </>
                )}
              </div>
              {pantoneState.lastRevocationTimestamp && (
                <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <strong>Access Previously Revoked:</strong> Last revocation on {new Date(pantoneState.lastRevocationTimestamp).toLocaleString()}
                </div>
              )}
              <PantoneLicenseModal
                isOpen={licenseOpen}
                onAccept={() => {
                  acceptLicense();
                  setLicenseOpen(false);
                  if (pendingLogin) {
                    setPendingLogin(false);
                    void login();
                  }
                }}
                onDecline={() => {
                  setLicenseOpen(false);
                  setPendingLogin(false);
                }}
              />
            </section>
            <PluginManagerPanel />
          </div>
        )}

        {/* About — last item, separated by divider */}
        <hr className="border-gray-200 dark:border-gray-700 my-6" />
        <button
          type="button"
          onClick={() => setShowAbout(true)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <Info className="w-4 h-4" />
          About {aboutConfig.app.name}
        </button>
      </div>

      <AboutModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        config={aboutConfig}
      />
    </div>
  );
}
